import React, { useEffect, useRef, useState } from 'react';
import {
  BookingSite,
  BookingSitePhoto,
  deleteBookingSitePhoto,
  deleteBookingSiteLogo,
  emptyBookingSite,
  getBookingSite,
  publishBookingSite,
  reorderBookingSitePhotos,
  saveBookingSite,
  uploadBookingSitePhoto,
  uploadBookingSiteLogo,
} from '../api.bookingSite';
import { PaymentMethod, listPaymentMethods } from '../api.settings';

const getToken = () => localStorage.getItem('token') || '';

// ── Predefined amenity categories ────────────────────────────────────────────
const AMENITY_CATEGORIES: Record<string, string[]> = {
  'Kuchyně a jídlo': ['Myčka', 'Mikrovlnka', 'Trouba', 'Kávovar', 'Toastovač', 'Lednice', 'Varná konvice', 'Nádobí a příbory'],
  'Obývací pokoj a ložnice': ['TV', 'WiFi', 'Klimatizace', 'Topení', 'Pracovní stůl', 'Žehlička', 'Fén', 'Pračka', 'Sušička'],
  'Koupelna': ['Vana', 'Sprcha', 'Ručníky', 'Mýdlo a šampon'],
  'Bezpečnost': ['Detektor kouře', 'Detektor CO', 'Hasicí přístroj', 'Lékárnička', 'Trezor'],
  'Venkovní prostory': ['Balkon / Terasa', 'BBQ gril', 'Zahrada', 'Parkování', 'Bazén', 'Vířivka'],
  'Přístupnost': ['Výtah', 'Bezbariérový přístup', 'Přízemí'],
  'Služby': ['Snídaně', 'Denní úklid', 'Uvítací košík', 'Transfer z letiště'],
};

const DAYS = [
  { key: 'MONDAY', short: 'Po' }, { key: 'TUESDAY', short: 'Út' },
  { key: 'WEDNESDAY', short: 'St' }, { key: 'THURSDAY', short: 'Čt' },
  { key: 'FRIDAY', short: 'Pá' }, { key: 'SATURDAY', short: 'So' },
  { key: 'SUNDAY', short: 'Ne' },
];

const BED_TYPES = [
  { value: 'double', label: 'Dvojlůžko' },
  { value: 'single', label: 'Jednolůžko' },
  { value: 'queen',  label: 'Queen' },
  { value: 'king',   label: 'King' },
  { value: 'sofa',   label: 'Rozkládací pohovka' },
  { value: 'bunk',   label: 'Patrová postel' },
  { value: 'floor',  label: 'Futon / podlaha' },
];

interface BedRow { type: string; count: number; }
interface FeeRow { name: string; amount: number; }
type TabId = 'general' | 'amenities' | 'pricing' | 'photos';

function parseAmenities(json: string | null): string[] {
  try { return json ? JSON.parse(json) : []; } catch { return []; }
}
function parseBeds(json: string | null): BedRow[] {
  try { return json ? JSON.parse(json) : []; } catch { return []; }
}
function serializeBeds(beds: BedRow[]): string | null {
  return beds.length ? JSON.stringify(beds) : null;
}
function parseFees(json: string | null): FeeRow[] {
  try { return json ? JSON.parse(json) : []; } catch { return []; }
}
function serializeFees(fees: FeeRow[]): string | null {
  return fees.length ? JSON.stringify(fees) : null;
}
interface RaisedPriceConfig { amount: string; days: string[]; }

function parseRaisedPrice(json: string | null): RaisedPriceConfig {
  try {
    if (!json) return { amount: '', days: [] };
    const parsed = JSON.parse(json);
    // New format: { price, days }
    if (!Array.isArray(parsed) && parsed.price !== undefined) {
      return { amount: String(parsed.price), days: parsed.days || [] };
    }
    // Old per-day format: [{ day, price }] — migrate: take price from first entry, collect days
    if (Array.isArray(parsed) && parsed.length > 0) {
      return { amount: String(parsed[0].price), days: parsed.map((r: any) => r.day) };
    }
  } catch {}
  return { amount: '', days: [] };
}
function serializeRaisedPrice(cfg: RaisedPriceConfig): string | null {
  if (!cfg.amount.trim() || cfg.days.length === 0) return null;
  return JSON.stringify({ price: Number(cfg.amount), days: cfg.days });
}

// ── Sub-section header ─────────────────────────────────────────────────────────
const SubHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div style={{ marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #e2e8f0' }}>
    <h4 style={{ margin: 0, color: '#2d3748', fontSize: '0.95rem', fontWeight: 700 }}>{title}</h4>
    {subtitle && <p style={{ margin: '3px 0 0', fontSize: '0.8rem', color: '#718096' }}>{subtitle}</p>}
  </div>
);

// ── Tab navigation ─────────────────────────────────────────────────────────────
const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'general',   label: 'Obecné',          icon: '🏠' },
  { id: 'amenities', label: 'Vybavení',         icon: '✨' },
  { id: 'pricing',   label: 'Ceny a platby',    icon: '💰' },
  { id: 'photos',    label: 'Fotky',            icon: '📷' },
];

// ── Main component ─────────────────────────────────────────────────────────────
interface Props { apartmentId: number; }

const DirectBookingTab: React.FC<Props> = ({ apartmentId }) => {
  const [site, setSite] = useState<BookingSite>(emptyBookingSite(apartmentId));
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const fileRef = useRef<HTMLInputElement>(null);
  const logoRef = useRef<HTMLInputElement>(null);

  const [amenities, setAmenities] = useState<string[]>([]);
  const [raisedPrice, setRaisedPrice] = useState<RaisedPriceConfig>({ amount: '', days: [] });
  const [beds, setBeds] = useState<BedRow[]>([]);
  const [fees, setFees] = useState<FeeRow[]>([]);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<PaymentMethod[]>([]);

  useEffect(() => {
    listPaymentMethods().then(setAvailablePaymentMethods).catch(() => {});
  }, []);

  useEffect(() => {
    getBookingSite(apartmentId, getToken())
      .then(data => {
        setSite(data);
        setAmenities(parseAmenities(data.amenitiesJson));
        setRaisedPrice(parseRaisedPrice(data.raisedPriceJson));
        setBeds(parseBeds(data.bedsJson));
        setFees(parseFees(data.additionalFeesJson));
        setLoaded(true);
      })
      .catch(err => {
        if (err.message === 'not_found') {
          setSite(emptyBookingSite(apartmentId));
          setLoaded(true);
        } else {
          setError('Nepodařilo se načíst nastavení webu přímé rezervace.');
          setLoaded(true);
        }
      });
  }, [apartmentId]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setSite(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : (value === '' ? null : value) }));
  };

  const onNumberChange = (name: keyof BookingSite) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setSite(prev => ({ ...prev, [name]: v === '' ? null : Number(v) }));
  };

  const toggleAmenity = (amenity: string) => {
    setAmenities(prev => prev.includes(amenity) ? prev.filter(a => a !== amenity) : [...prev, amenity]);
  };

  const buildPayload = (): BookingSite => ({
    ...site,
    amenitiesJson: amenities.length ? JSON.stringify(amenities) : null,
    raisedPriceJson: serializeRaisedPrice(raisedPrice),
    bedsJson: serializeBeds(beds),
    additionalFeesJson: serializeFees(fees),
  });

  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const updated = await saveBookingSite(apartmentId, buildPayload(), getToken());
      setSite(updated);
      setAmenities(parseAmenities(updated.amenitiesJson));
      setRaisedPrice(parseRaisedPrice(updated.raisedPriceJson));
      setSuccess('Nastavení uloženo.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e.message || 'Chyba při ukládání.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true); setError(null); setSuccess(null);
    try {
      await saveBookingSite(apartmentId, buildPayload(), getToken());
      const updated = await publishBookingSite(apartmentId, getToken());
      setSite(updated);
      setSuccess('Web publikován! Přejděte na https://' + updated.slug + '.guestbook.cz');
      setTimeout(() => setSuccess(null), 8000);
    } catch (e: any) {
      setError(e.message || 'Chyba při publikování.');
    } finally {
      setPublishing(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!site.slug) { setError('Nejdříve zadejte slug a uložte web.'); return; }
    setUploading(true); setError(null);
    try {
      const photo = await uploadBookingSitePhoto(apartmentId, file, null, getToken());
      setSite(prev => ({ ...prev, photos: [...prev.photos, photo] }));
    } catch (e: any) {
      setError(e.message || 'Chyba při nahrávání fotky.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handlePhotoDelete = async (photoId: number) => {
    if (!window.confirm('Smazat tuto fotku?')) return;
    try {
      await deleteBookingSitePhoto(apartmentId, photoId, getToken());
      setSite(prev => ({ ...prev, photos: prev.photos.filter(p => p.id !== photoId) }));
    } catch (e: any) {
      setError(e.message || 'Chyba při mazání fotky.');
    }
  };

  const handlePhotoMove = async (index: number, dir: -1 | 1) => {
    const newPhotos = [...site.photos];
    const swapIdx = index + dir;
    if (swapIdx < 0 || swapIdx >= newPhotos.length) return;
    [newPhotos[index], newPhotos[swapIdx]] = [newPhotos[swapIdx], newPhotos[index]];
    setSite(prev => ({ ...prev, photos: newPhotos }));
    try {
      await reorderBookingSitePhotos(apartmentId, newPhotos.map(p => p.id), getToken());
    } catch {
      setError('Chyba při řazení fotek.');
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!site.id) { setError('Nejdříve uložte web.'); return; }
    setUploadingLogo(true); setError(null);
    try {
      const updated = await uploadBookingSiteLogo(apartmentId, file, getToken());
      setSite(prev => ({ ...prev, logoUrl: updated.logoUrl }));
    } catch (e: any) {
      setError(e.message || 'Chyba při nahrávání loga.');
    } finally {
      setUploadingLogo(false);
      if (logoRef.current) logoRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Smazat logo?')) return;
    try {
      const updated = await deleteBookingSiteLogo(apartmentId, getToken());
      setSite(prev => ({ ...prev, logoUrl: updated.logoUrl }));
    } catch (e: any) {
      setError(e.message || 'Chyba při mazání loga.');
    }
  };

  if (!loaded) return <div style={{ padding: 24, color: '#718096' }}>Načítám…</div>;

  const tabBtnStyle = (id: TabId): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '9px 16px', border: 'none', cursor: 'pointer',
    fontSize: '0.87rem', fontWeight: 600, borderRadius: 8,
    background: activeTab === id ? 'var(--brand, #3182ce)' : 'transparent',
    color: activeTab === id ? '#fff' : '#4a5568',
    transition: 'background 0.15s, color 0.15s',
  });

  return (
    <div style={{ maxWidth: 780 }}>

      {/* ── Status banner ──────────────────────────────────────────────────────── */}
      <div style={{
        padding: '10px 14px', borderRadius: 8, marginBottom: 14,
        background: site.isPublished ? '#f0fff4' : '#fffaf0',
        border: `1px solid ${site.isPublished ? '#9ae6b4' : '#fbd38d'}`,
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <span style={{ fontWeight: 700, color: site.isPublished ? '#276749' : '#744210' }}>
          {site.isPublished ? '✅ Publikováno' : '⚠️ Nepublikováno'}
        </span>
        {site.isPublished && site.slug && (
          <a href={`https://${site.slug}.guestbook.cz`} target="_blank" rel="noreferrer"
            style={{ fontSize: '0.85rem', color: '#2b6cb0' }}>
            https://{site.slug}.guestbook.cz ↗
          </a>
        )}
        {site.htmlGeneratedAt && (
          <span style={{ fontSize: '0.8rem', color: '#718096', marginLeft: 'auto' }}>
            Vygenerováno: {new Date(site.htmlGeneratedAt).toLocaleString('cs-CZ')}
          </span>
        )}
      </div>

      {error && <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', color: '#c53030', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{error}</div>}
      {success && <div style={{ background: '#f0fff4', border: '1px solid #9ae6b4', color: '#276749', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{success}</div>}

      {/* ── Tab navigation ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 4, padding: '6px 6px',
        background: '#f7fafc', borderRadius: 12, border: '1px solid #e2e8f0',
        marginBottom: 20,
      }}>
        {TABS.map(tab => (
          <button key={tab.id} style={tabBtnStyle(tab.id)} onClick={() => setActiveTab(tab.id)}>
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          TAB 1: Obecné (General)
      ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'general' && (
        <div className="space-y-6">

          <SubHeader title="Identifikace webu" />
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Slug (URL) <span style={{ color: '#c53030' }}>*</span></label>
              <input className="form-control" name="slug" value={site.slug || ''} onChange={onChange}
                placeholder="napr. vila-na-horach" maxLength={100} />
              <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: 4 }}>
                Bude dostupné na https://<strong>{site.slug || 'slug'}</strong>.guestbook.cz
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">Název nemovitosti</label>
              <input className="form-control" name="title" value={site.title || ''} onChange={onChange}
                placeholder="Krásný byt v centru Prahy" maxLength={255} />
            </div>
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Úvod / popis</label>
              <textarea className="form-control" name="introduction" value={site.introduction || ''} onChange={onChange}
                placeholder="Krátký popis, který hosté uvidí jako první…" style={{ minHeight: 100 }} />
            </div>
          </div>

          <SubHeader title="Typ nemovitosti" />
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Kategorie</label>
              <select className="form-control" name="category" value={site.category || ''} onChange={onChange}>
                <option value="">– vyberte –</option>
                <option value="apartment">Apartmán</option>
                <option value="house">Dům</option>
                <option value="studio">Studio</option>
                <option value="room">Pokoj</option>
                <option value="villa">Vila</option>
                <option value="cottage">Chata</option>
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Typ nabídky</label>
              <select className="form-control" name="listingType" value={site.listingType || ''} onChange={onChange}>
                <option value="">– vyberte –</option>
                <option value="entire_place">Celý objekt</option>
                <option value="private_room">Soukromý pokoj</option>
                <option value="shared_room">Sdílený pokoj</option>
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Počet ložnic</label>
              <input className="form-control" type="number" min={0} value={site.bedroomsCount ?? ''} onChange={onNumberChange('bedroomsCount')} />
            </div>
            <div className="form-field">
              <label className="form-label">Počet koupelen</label>
              <input className="form-control" type="number" min={0} step={0.5} value={site.bathroomsCount ?? ''} onChange={onNumberChange('bathroomsCount')} />
            </div>
            <div className="form-field">
              <label className="form-label">Max. počet hostů</label>
              <input className="form-control" type="number" min={1} value={site.maxGuests ?? ''} onChange={onNumberChange('maxGuests')} />
            </div>
            <div className="form-field">
              <label className="form-label">Lůžka</label>
              {beds.map((bed, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <select value={bed.type}
                    onChange={e => setBeds(prev => prev.map((b, j) => j === i ? { ...b, type: e.target.value } : b))}
                    style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem' }}>
                    {BED_TYPES.map(bt => <option key={bt.value} value={bt.value}>{bt.label}</option>)}
                  </select>
                  <input type="number" min={1} max={10} value={bed.count}
                    onChange={e => setBeds(prev => prev.map((b, j) => j === i ? { ...b, count: Number(e.target.value) } : b))}
                    style={{ width: 60, padding: '6px 8px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem' }} />
                  <button type="button" onClick={() => setBeds(prev => prev.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}>✕</button>
                </div>
              ))}
              <button type="button" onClick={() => setBeds(prev => [...prev, { type: 'double', count: 1 }])}
                style={{ marginTop: 4, padding: '5px 12px', background: '#f7fafc', border: '1px dashed #cbd5e0', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', color: '#4a5568' }}>
                + Přidat lůžko
              </button>
            </div>
          </div>

          <SubHeader title="Poloha" />
          <div className="form-grid">
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Ulice a číslo popisné</label>
              <input className="form-control" name="streetAddress" value={site.streetAddress || ''} onChange={onChange} />
            </div>
            <div className="form-field">
              <label className="form-label">Město</label>
              <input className="form-control" name="city" value={site.city || ''} onChange={onChange} />
            </div>
            <div className="form-field">
              <label className="form-label">Kraj / stát</label>
              <input className="form-control" name="state" value={site.state || ''} onChange={onChange} />
            </div>
            <div className="form-field">
              <label className="form-label">PSČ</label>
              <input className="form-control" name="postalCode" value={site.postalCode || ''} onChange={onChange} maxLength={20} />
            </div>
            <div className="form-field">
              <label className="form-label">Stát</label>
              <input className="form-control" name="country" value={site.country || ''} onChange={onChange} placeholder="Česká republika" />
            </div>
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" name="showExactAddress" checked={!!site.showExactAddress} onChange={onChange} />
                Zobrazovat přesnou adresu hostům
              </label>
            </div>
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Pokyny pro příjezd</label>
              <textarea className="form-control" name="arrivalInstructions" value={site.arrivalInstructions || ''} onChange={onChange}
                placeholder="Jak se dostat k objektu, kde zaparkovat…" style={{ minHeight: 80 }} />
            </div>
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Okolí (popis)</label>
              <textarea className="form-control" name="neighborhoodHighlights" value={site.neighborhoodHighlights || ''} onChange={onChange}
                placeholder="Zajímavá místa v okolí, restaurace, příroda…" style={{ minHeight: 60 }} />
            </div>
          </div>

          <SubHeader title="Pravidla pobytu" />
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Check-in od</label>
              <input className="form-control" type="time" name="checkinTime" value={site.checkinTime || ''} onChange={onChange} />
            </div>
            <div className="form-field">
              <label className="form-label">Check-out do</label>
              <input className="form-control" type="time" name="checkoutTime" value={site.checkoutTime || ''} onChange={onChange} />
            </div>
            <div className="form-field">
              <label className="form-label">Dostupnost (měsíce dopředu)</label>
              <input className="form-control" type="number" min={1} max={24} name="availabilityMonths"
                value={site.availabilityMonths} onChange={onNumberChange('availabilityMonths')} />
            </div>
            <div className="form-field">
              <label className="form-label">Storno podmínky</label>
              <select className="form-control" name="cancellationPolicy" value={site.cancellationPolicy} onChange={onChange}>
                <option value="FLEXIBLE">Flexibilní – plné vrácení do 24h před příjezdem</option>
                <option value="FIRM">Mírné – plné vrácení do 5 dní před příjezdem</option>
                <option value="STRICT_1">Přísné 1 – 50% vrácení do 7 dní před příjezdem</option>
                <option value="STRICT_2">Přísné 2 – 50% vrácení do 14 dní před příjezdem</option>
                <option value="STRICT_3">Přísné 3 – 50% vrácení do 21 dní před příjezdem</option>
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" name="petsAllowed" checked={!!site.petsAllowed} onChange={onChange} />
                Mazlíčci povoleni
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" name="partiesAllowed" checked={!!site.partiesAllowed} onChange={onChange} />
                Oslavy povoleny
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" name="smokingAllowed" checked={!!site.smokingAllowed} onChange={onChange} />
                Kouření povoleno
              </label>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          TAB 2: Vybavení (Amenities)
      ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'amenities' && (
        <div>
          <SubHeader title="Vybavení a služby" subtitle={`Vybráno: ${amenities.length} položek`} />
          {Object.entries(AMENITY_CATEGORIES).map(([cat, items]) => (
            <div key={cat} style={{ marginBottom: 18 }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#4a5568', marginBottom: 8 }}>{cat}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {items.map(item => (
                  <label key={item} style={{
                    display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                    padding: '5px 12px', borderRadius: 20, fontSize: '0.84rem',
                    background: amenities.includes(item) ? 'var(--brand)' : '#edf2f7',
                    color: amenities.includes(item) ? '#fff' : '#4a5568',
                    border: '1px solid ' + (amenities.includes(item) ? 'var(--brand)' : '#e2e8f0'),
                    userSelect: 'none', transition: 'background 0.12s',
                  }}>
                    <input type="checkbox" checked={amenities.includes(item)}
                      onChange={() => toggleAmenity(item)} style={{ display: 'none' }} />
                    {amenities.includes(item) ? '✓ ' : ''}{item}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          TAB 3: Ceny a platby (Pricing)
      ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'pricing' && (
        <div>
          <SubHeader title="Základní ceny" />
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Základní cena za noc (Kč)</label>
              <input className="form-control" type="number" min={0} step={0.01} value={site.basePrice ?? ''} onChange={onNumberChange('basePrice')} />
            </div>
            <div className="form-field">
              <label className="form-label">Poplatek za úklid (Kč)</label>
              <input className="form-control" type="number" min={0} step={0.01} value={site.cleaningFee ?? ''} onChange={onNumberChange('cleaningFee')} />
            </div>
            <div className="form-field">
              <label className="form-label">Poplatek za mazlíčka (Kč)</label>
              <input className="form-control" type="number" min={0} step={0.01} value={site.petFee ?? ''} onChange={onNumberChange('petFee')} />
            </div>
            <div className="form-field">
              <label className="form-label">Příplatek za extra hosta (Kč)</label>
              <input className="form-control" type="number" min={0} step={0.01} value={site.extraGuestFee ?? ''} onChange={onNumberChange('extraGuestFee')} />
            </div>
            <div className="form-field">
              <label className="form-label">Příplatek od počtu hostů</label>
              <input className="form-control" type="number" min={1} value={site.extraGuestAfter ?? ''} onChange={onNumberChange('extraGuestAfter')} placeholder="napr. 4" />
            </div>
          </div>

          <SubHeader title="Zvýšená cena (volitelné)" subtitle="Např. vyšší cena o víkendu. Nastavte cenu a vyberte dny." />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
            <div className="form-field" style={{ flex: '0 0 180px' }}>
              <label className="form-label">Zvýšená cena za noc (Kč)</label>
              <input className="form-control" type="number" min={0} step={0.01}
                placeholder="napr. 2 400"
                value={raisedPrice.amount}
                onChange={e => setRaisedPrice(prev => ({ ...prev, amount: e.target.value }))} />
            </div>
            <div className="form-field" style={{ flex: 1, minWidth: 240 }}>
              <label className="form-label">Platí pro tyto dny</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                {DAYS.map(d => {
                  const on = raisedPrice.days.includes(d.key);
                  return (
                    <button key={d.key} type="button"
                      onClick={() => setRaisedPrice(prev => ({
                        ...prev,
                        days: on ? prev.days.filter(x => x !== d.key) : [...prev.days, d.key],
                      }))}
                      style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 700,
                        cursor: 'pointer', border: '1px solid',
                        background: on ? 'var(--brand)' : '#edf2f7',
                        color: on ? '#fff' : '#4a5568',
                        borderColor: on ? 'var(--brand)' : '#e2e8f0',
                        transition: 'background 0.12s',
                      }}>
                      {d.short}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <SubHeader title="Další poplatky" subtitle="Zobrazí se hostovi v rekapitulaci ceny." />
            {fees.map((fee, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <input type="text" placeholder="Název (např. Parkování)" value={fee.name}
                  onChange={e => setFees(prev => prev.map((f, j) => j === i ? { ...f, name: e.target.value } : f))}
                  style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem' }} />
                <input type="number" min={0} step={0.01} placeholder="Kč" value={fee.amount}
                  onChange={e => setFees(prev => prev.map((f, j) => j === i ? { ...f, amount: Number(e.target.value) } : f))}
                  style={{ width: 80, padding: '6px 8px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem' }} />
                <button type="button" onClick={() => setFees(prev => prev.filter((_, j) => j !== i))}
                  style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}>✕</button>
              </div>
            ))}
            <button type="button" onClick={() => setFees(prev => [...prev, { name: '', amount: 0 }])}
              style={{ marginTop: 4, padding: '5px 12px', background: '#f7fafc', border: '1px dashed #cbd5e0', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', color: '#4a5568' }}>
              + Přidat poplatek
            </button>
          </div>

          <div style={{ marginTop: 20 }}>
            <SubHeader title="Platební metody" />
            <div style={{ background: '#fffaf0', border: '1px solid #fbd38d', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.83rem', color: '#744210' }}>
              ⚠️ Vybrané platební metody jsou zaslány hostovi po schválení poptávky — nejsou viditelné na veřejném webu.
            </div>
            {availablePaymentMethods.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: '0.88rem', padding: '10px 0' }}>
                Zatím nemáte žádné platební metody. Přidejte je v{' '}
                <a href="/settings/payment-methods" style={{ color: '#2563eb' }}>Nastavení → Platební metody</a>.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {availablePaymentMethods.map(pm => {
                  const selected = (site.paymentMethodIds || []).includes(pm.id);
                  return (
                    <label key={pm.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '10px 12px', borderRadius: 8, border: `1px solid ${selected ? '#2563eb' : '#e2e8f0'}`, background: selected ? '#eff6ff' : '#fff' }}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={e => {
                          setSite(prev => ({
                            ...prev,
                            paymentMethodIds: e.target.checked
                              ? [...(prev.paymentMethodIds || []), pm.id]
                              : (prev.paymentMethodIds || []).filter(id => id !== pm.id),
                          }));
                        }}
                        style={{ marginTop: 2 }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{pm.name}</div>
                        {pm.detailsJson && <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2, whiteSpace: 'pre-wrap' }}>{pm.detailsJson}</div>}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          TAB 4: Fotky (Photos)
      ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'photos' && (
        <div>
          <SubHeader title="Fotky nemovitosti" subtitle="Doporučujeme min. 5 fotek. První fotka je titulní." />

          {!site.id ? (
            <div style={{
              padding: '24px 20px', borderRadius: 10, border: '1px dashed #cbd5e0',
              textAlign: 'center', color: '#718096', fontSize: '0.9rem',
            }}>
              💡 Nejdříve uložte web v záložce <strong>Obecné</strong>, poté zde můžete nahrávat fotky.
            </div>
          ) : (
            <div>
              {site.photos.length === 0 ? (
                <div style={{
                  padding: '32px 20px', borderRadius: 10, border: '1px dashed #cbd5e0',
                  textAlign: 'center', color: '#a0aec0', marginBottom: 16,
                }}>
                  📷 Zatím žádné fotky. Přidejte první fotku níže.
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                  {site.photos.map((photo, idx) => (
                    <div key={photo.id} style={{ position: 'relative', width: 130, flexShrink: 0 }}>
                      {idx === 0 && (
                        <div style={{
                          position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,0.65)',
                          color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px',
                          borderRadius: 4, zIndex: 1,
                        }}>TITULNÍ</div>
                      )}
                      <img src={photo.url} alt={photo.caption || ''} style={{
                        width: '100%', height: 96, objectFit: 'cover', borderRadius: 8,
                        display: 'block', border: '1px solid #e2e8f0',
                      }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, gap: 2 }}>
                        <button onClick={() => handlePhotoMove(idx, -1)} disabled={idx === 0}
                          style={{ flex: 1, fontSize: '0.7rem', padding: '3px 4px', border: '1px solid #e2e8f0', borderRadius: 5, cursor: 'pointer', background: '#f7fafc', opacity: idx === 0 ? 0.4 : 1 }}>◀</button>
                        <button onClick={() => handlePhotoDelete(photo.id)}
                          style={{ flex: 1, fontSize: '0.7rem', padding: '3px 4px', border: '1px solid #fed7d7', borderRadius: 5, cursor: 'pointer', background: '#fff5f5', color: '#c53030' }}>✕ Smazat</button>
                        <button onClick={() => handlePhotoMove(idx, 1)} disabled={idx === site.photos.length - 1}
                          style={{ flex: 1, fontSize: '0.7rem', padding: '3px 4px', border: '1px solid #e2e8f0', borderRadius: 5, cursor: 'pointer', background: '#f7fafc', opacity: idx === site.photos.length - 1 ? 0.4 : 1 }}>▶</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#f7fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                <button className="app-btn app-btn-blue app-btn-standard" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? '⏳ Nahrávám…' : '+ Přidat fotku'}
                </button>
                <span style={{ fontSize: '0.8rem', color: '#718096' }}>
                  Max. 10 MB, JPEG/PNG &nbsp;·&nbsp; {site.photos.length} / 20 fotek
                </span>
              </div>
            </div>
          )}

          {/* ── Branding ──────────────────────────────────────────────────────── */}
          <div style={{ marginTop: 28 }}>
            <SubHeader title="Vzhled webu" subtitle="Logo, barvy a ikona záložky prohlížeče." />

            {/* Logo */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: 8 }}>Logo</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {site.logoUrl ? (
                  <img src={site.logoUrl} alt="Logo" style={{
                    height: 56, maxWidth: 140, objectFit: 'contain',
                    borderRadius: 8, border: '1px solid #e2e8f0', background: '#f7fafc', padding: 4,
                  }} />
                ) : (
                  <div style={{
                    width: 56, height: 56, borderRadius: 8, border: '2px dashed #cbd5e0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.4rem', color: '#a0aec0', background: '#f7fafc',
                  }}>🏠</div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
                  <button className="app-btn app-btn-blue app-btn-standard"
                    style={{ fontSize: '0.82rem', padding: '5px 12px' }}
                    onClick={() => logoRef.current?.click()} disabled={uploadingLogo || !site.id}>
                    {uploadingLogo ? '⏳ Nahrávám…' : site.logoUrl ? '🔄 Změnit logo' : '+ Nahrát logo'}
                  </button>
                  {site.logoUrl && (
                    <button onClick={handleLogoDelete}
                      style={{ fontSize: '0.82rem', padding: '5px 12px', background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 6, cursor: 'pointer', color: '#c53030' }}>
                      ✕ Smazat logo
                    </button>
                  )}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#718096', lineHeight: 1.5 }}>
                  Doporučená velikost: 400 × 400 px<br />Formát: PNG, JPG
                </div>
              </div>
            </div>

            {/* Color theme */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: 8 }}>Barevné téma</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  { key: 'emerald', label: 'Zelená',  color: '#16a34a' },
                  { key: 'blue',    label: 'Modrá',   color: '#2563eb' },
                  { key: 'violet',  label: 'Fialová', color: '#7c3aed' },
                  { key: 'rose',    label: 'Růžová',  color: '#e11d48' },
                ].map(theme => {
                  const active = (site.colorTheme || 'emerald') === theme.key;
                  return (
                    <button key={theme.key} type="button"
                      onClick={() => setSite(prev => ({ ...prev, colorTheme: theme.key }))}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
                        border: active ? `2px solid ${theme.color}` : '2px solid #e2e8f0',
                        background: active ? `${theme.color}12` : '#f7fafc',
                        transition: 'border 0.12s, background 0.12s',
                      }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: theme.color }} />
                      <span style={{ fontSize: '0.78rem', fontWeight: active ? 700 : 400, color: active ? theme.color : '#4a5568' }}>
                        {theme.label}
                      </span>
                      {active && <span style={{ fontSize: '0.7rem', color: theme.color }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Favicon emoji */}
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: 4 }}>Ikona záložky (emoji)</div>
              <div style={{ fontSize: '0.78rem', color: '#718096', marginBottom: 6 }}>Zadejte jedno emoji, které se zobrazí jako favicon v prohlížeči.</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input className="form-control" name="faviconEmoji" value={site.faviconEmoji || ''} onChange={onChange}
                  placeholder="🏠" maxLength={4}
                  style={{ width: 80, fontSize: '1.4rem', textAlign: 'center', padding: '4px 8px' }} />
                {site.faviconEmoji && (
                  <span style={{ fontSize: '2rem' }}>{site.faviconEmoji}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Actions (always visible) ───────────────────────────────────────────── */}
      <div style={{ marginTop: 24, display: 'flex', gap: 10, flexWrap: 'wrap', borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
        <button className="app-btn app-btn-blue app-btn-standard" onClick={handleSave} disabled={saving || publishing}>
          {saving ? 'Ukládám…' : '💾 Uložit'}
        </button>
        <button className="app-btn app-btn-standard" onClick={handlePublish} disabled={saving || publishing || !site.slug}
          style={{ background: '#38a169', color: '#fff', borderColor: '#38a169' }}>
          {publishing ? 'Publikuji…' : site.isPublished ? '🔄 Uložit a znovu publikovat' : '🚀 Uložit a publikovat'}
        </button>
        {site.isPublished && site.slug && (
          <a href={`https://${site.slug}.guestbook.cz/calendar.ics`} target="_blank" rel="noreferrer"
            className="app-btn app-btn-standard" style={{ textDecoration: 'none' }}>
            📅 iCal export
          </a>
        )}
      </div>
    </div>
  );
};

export default DirectBookingTab;
